import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { GroupService } from '../../../core/services/group.service';
import { AssignableMember, taskAssignableMembers } from '../../../core/utils/task-assignees';

export interface TaskDialogData {
  date?: any;
  dueDate?: any;
  task?: string;
  notes?: string;
  assigneeUserIds?: string[];
  /** The AI-output screens show the "MedicAI can make mistakes" hint and offer find/replace on the notes. */
  aiGenerated?: boolean;
}

export interface TaskDialogResult {
  date: any;
  dueDate: any;
  task: string;
  notes: string;
  /** Empty = the whole practice. `tasks/task-assignment.md`. */
  assigneeUserIds: string[];
}

/**
 * The quick "add a task" dialog — ONE component for the three screens that each carried their own copy of
 * the same template: discharge, consultation AI output and ward-round notes. The full editor is the routed
 * `PatientTaskDetailComponent`, which has the same "Assigned to" field.
 *
 * Closes with a `TaskDialogResult`, or `null` on cancel.
 */
@Component({
  selector: 'app-task-dialog',
  templateUrl: './task-dialog.component.html',
  styleUrls: ['./task-dialog.component.scss']
})
export class TaskDialogComponent implements OnInit, OnDestroy {
  todayDate = moment();
  members: AssignableMember[] = [];
  model: TaskDialogResult;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<TaskDialogComponent, TaskDialogResult | null>,
    private groupService: GroupService,
    @Inject(MAT_DIALOG_DATA) public data: TaskDialogData,
  ) {
    const d = data || {};
    this.model = {
      date: d.date ? moment(d.date) : moment(),
      dueDate: d.dueDate || '',
      task: d.task || '',
      notes: d.notes || '',
      assigneeUserIds: Array.isArray(d.assigneeUserIds) ? [...d.assigneeUserIds] : [],
    };
  }

  ngOnInit(): void {
    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[] | null) => {
        if (users == null) {
          // Nothing has loaded the member list yet on this screen.
          this.groupService.getGroupUsers().pipe(takeUntil(this.destroy$)).subscribe();
          return;
        }
        this.members = taskAssignableMembers(users);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canSave(): boolean {
    return !!this.model.date && !!String(this.model.task || '').trim();
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    if (!this.canSave) return;
    this.dialogRef.close({ ...this.model, assigneeUserIds: [...this.model.assigneeUserIds] });
  }
}
